import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  XMarkIcon, 
  BuildingStorefrontIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon,
  ArrowRightOnRectangleIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { apiService } from '../services/api';
import { buildRestaurantFrontendOrigin } from '../utils/restaurantUtils';

interface PricingPlansModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPlanSelect?: (plan: 'basic' | 'pro') => void; // Optional - plan updates happen automatically in backend
  showOnlyPro?: boolean;
  isMandatory?: boolean;
}

interface PlanData {
  id: 'basic' | 'pro';
  planId?: string; // API plan ID
  name: string;
  price?: string;
  period?: string;
  color: 'primary' | 'success';
  description: string;
  features: string[];
  buttonText: string;
  iconName: 'store' | 'restaurant';
  popular: boolean;
  comingSoon?: boolean; // Set to true if plan.status is 'disabled'
}

const PricingPlansModal: React.FC<PricingPlansModalProps> = ({ isOpen, onClose, showOnlyPro = false, isMandatory = false }) => {
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'pro' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [plansData, setPlansData] = useState<PlanData[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [isActivatingTrial, setIsActivatingTrial] = useState(false);
  const { restaurantDetails, loadRestaurantDetails, logout } = useAuth();
  const { showError, showSuccess } = useToast();
  const hasLoadedDetailsRef = useRef(false);
  const hasFetchedPlansRef = useRef(false);
  const enablePayment = Boolean((restaurantDetails as any)?.enable_payment);
  const enableSkip = Boolean((restaurantDetails as any)?.enable_skip);
  const restaurantDetailsRef = useRef<any>(restaurantDetails);

  useEffect(() => {
    restaurantDetailsRef.current = restaurantDetails;
  }, [restaurantDetails]);

  const waitForPlanSync = useCallback(
    async (
      validator: (plan: 'basic' | 'pro' | 'no active plan' | 'no_plan' | undefined) => boolean,
      maxAttempts: number = 20,
      delayMs: number = 2000
    ): Promise<boolean> => {
      const getLatestPlan = (): 'basic' | 'pro' | 'no active plan' | 'no_plan' | undefined => {
        const refPlan = (restaurantDetailsRef.current as any)?.plan;
        if (refPlan) {
          return refPlan;
        }

        try {
          const storedDetails = localStorage.getItem('restaurantDetails');
          if (storedDetails) {
            const parsed = JSON.parse(storedDetails);
            return parsed?.plan;
          }
        } catch (error) {
          console.error('Failed to parse restaurantDetails from localStorage:', error);
        }

        return undefined;
      };

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        await loadRestaurantDetails(true);
        const latestPlan = getLatestPlan();

        if (validator(latestPlan)) {
          console.log(`Plan sync succeeded on attempt ${attempt} with plan:`, latestPlan);
          return true;
        }

        if (attempt < maxAttempts) {
          console.warn(`Plan sync attempt ${attempt} did not meet condition. Retrying in ${delayMs}ms...`);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }

      console.error('Plan sync failed after maximum attempts.');
      return false;
    },
    [loadRestaurantDetails]
  );

  const handlePlanSelect = async (plan: 'basic' | 'pro') => {
    setSelectedPlan(plan);
    setIsLoading(true);
    setError('');
    
    try {
      // Get the current plans (from state or default)
      const currentPlans = plansData.length > 0 ? plansData : getDefaultPlans();
      
      // Find the selected plan data with planId
      const selectedPlanData = currentPlans.find(p => p.id === plan);
      
      // Check if plan is coming soon (disabled)
      if (selectedPlanData?.comingSoon) {
        setError('This plan is coming soon and not available for selection yet.');
        setIsLoading(false);
        return;
      }
      
      // Check if enable_payment is true
      const enablePayment = (restaurantDetails as any)?.enable_payment;
      
      if (enablePayment) {
        if (!selectedPlanData || !selectedPlanData.planId) {
          setError('Plan ID not found. Please try again.');
          setIsLoading(false);
          return;
        }

        // Create subscription via API
        const subscriptionResponse = await apiService.createSubscription(selectedPlanData.planId);
        
        // Type assertion for response data
        const responseData = subscriptionResponse as any;
        
        if (!subscriptionResponse.status || !responseData.subscription_id) {
          setError((subscriptionResponse as any)?.message || 'Failed to create subscription. Please try again.');
          setIsLoading(false);
          return;
        }

        const subscription_id = responseData.subscription_id;

        // Initialize Razorpay checkout
        const razorpayKeyId = import.meta.env.RAZORPAY_KEY_ID;
        
        // Check if Razorpay is loaded
        if (!(window as any).Razorpay) {
          setError('Payment gateway not loaded. Please refresh and try again.');
          setIsLoading(false);
          return;
        }

        const options = {
          key: razorpayKeyId,
          subscription_id: subscription_id,
          name: 'Menunix',
          description: `Subscription for ${selectedPlanData.name}`,
          handler: async (response: any) => {
            setIsVerifyingPayment(true);
            try {
              console.log('Payment successful:', response);

              const verificationPayload = {
                razorpay_payment_id: response?.razorpay_payment_id,
                razorpay_subscription_id: response?.razorpay_subscription_id || subscription_id,
                razorpay_signature: response?.razorpay_signature,
              };

              const maxVerificationAttempts = 30;
              const verificationDelayMs = 3000;
              let verificationSuccess = false;
              let lastVerificationResult: any = null;

              for (let attempt = 1; attempt <= maxVerificationAttempts; attempt += 1) {
                const verificationResult = await apiService.verifySubscriptionPayment(verificationPayload);
                lastVerificationResult = verificationResult;
                console.log(`Verification attempt ${attempt} result:`, verificationResult);

                const isVerified =
                  verificationResult?.status === true ||
                  (typeof verificationResult === 'object' &&
                    verificationResult !== null &&
                    'success' in verificationResult &&
                    (verificationResult as any).success === true);

                if (isVerified) {
                  verificationSuccess = true;
                  break;
                }

                if (attempt < maxVerificationAttempts) {
                  console.warn(`Payment verification failed on attempt ${attempt}. Retrying in ${verificationDelayMs}ms...`);
                  await new Promise(resolve => setTimeout(resolve, verificationDelayMs));
                }
              }

              if (!verificationSuccess) {
                console.error('Payment verification failed after retries:', lastVerificationResult);
                const errorMessage = "The payment confirmation is taking longer than expected. Access will be granted automatically once it's complete. Please try again later or reload the page to apply the update.";
                setError(errorMessage);
                showError('Payment Verification', errorMessage);
                return;
              }

              console.log('Payment verification result:', lastVerificationResult);

              // Wait for backend webhook to update plan before proceeding
              const planSynced = await waitForPlanSync(latestPlan => latestPlan === plan);

              

              if (planSynced) {
                // Track subscription conversion
                // Push event to dataLayer if available (dataLayer is an array)
                if (Array.isArray((window as any).dataLayer)) {
                  (window as any).dataLayer.push({
                    event: "subscribe",
                  });
                }
                
                if (typeof (window as any).gtag === 'function') {
                  (window as any).gtag('event', 'conversion', {
                    'send_to': 'AW-17751140940/LdipCKKe5MQbEMzUs5BC'
                  });
                }
                
                
                if(plan === 'basic' && ['tables', 'dashboard', 'orders'].includes(localStorage.getItem('selectedTab')||"")) {
                localStorage.setItem('selectedTab', 'categories');
                }
                
                // Set flag to show "Your store is live" modal after reload
                localStorage.setItem('visitSiteModalPending', '1');
                
                console.log('Payment successful. Reloading to update your plan in 300ms.');
                onClose();
                console.log('Reloading page after payment success.');
                window.location.reload();
              } else {
                setError('Payment verified but plan update is still pending. Please refresh after a moment or contact support if it persists.');
                console.warn('Payment verified but plan sync did not complete in time.');
              }
            } catch (error) {
              console.error('Error updating plan after payment:', error);
              setError('Payment successful but failed to update plan. Please contact support.');
            } finally {
              setIsVerifyingPayment(false);
              setIsLoading(false);
            }
          },
          prefill: {
            contact: restaurantDetails?.email || '',
          },
          theme: {
            color: '#f97316'
          },
          modal: {
            ondismiss: () => {
              setIsLoading(false);
            }
          }
        };

        const razorpay = new (window as any).Razorpay(options);
        razorpay.open();

        // Handle payment failure
        razorpay.on('payment.failed', (response: any) => {
          console.error('Payment failed:', response);
          setError(`Payment failed: ${response.error?.description || 'Unknown error'}`);
          setIsVerifyingPayment(false);
          setIsLoading(false);
        });

      } else {
        // Payment not enabled; wait for backend to sync plan via webhook/other processes
        const planSynced = await waitForPlanSync(latestPlan => latestPlan === plan);

        if (planSynced) {
          // Track subscription conversion (when payment not enabled)
          // if (typeof (window as any).gtag === 'function') {
          //   (window as any).gtag('event', 'conversion', {
          //     'send_to': 'AW-813387003/40hNCOq-zcQbEPuZ7YMD'
          //   });
          // }
          
          onClose();
        } else {
          setError('Plan update is pending. Please try again shortly or contact support.');
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error selecting plan:', error);
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleClose = (force: boolean = false) => {
    if (!force && isMandatory && !hasActivePlan) {
      // Don't allow closing if modal is mandatory and user has no active plan
      return;
    }
    if (window.history.state?.modalOpen) {
      window.history.back();
    }
    onClose();
  };

  const handleTrialActivation = async () => {
    setIsActivatingTrial(true);
    setError('');
    
    try {
      const response = await apiService.enableTrial();
      
      if (response.status) {
        // Track trial conversion
        if (Array.isArray((window as any).dataLayer)) {
          (window as any).dataLayer.push({
            event: "trial_started",
          });
        }
        
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'conversion', {
            'send_to': 'AW-17751140940/wUWXCJ-e5MQbEMzUs5BC'
          });
        }
        
        // Force refresh restaurant details to reflect the trial status
        await loadRestaurantDetails(true);
        
        // Set flag to show "Your store is live" modal after reload
        try {
          localStorage.setItem('visitSiteModalPending', '1');
        } catch (_) {}
        
        showSuccess('Trial Activated', 'Your 15-day free trial has been activated! All features are now available. You can upgrade anytime during or after the trial period.');
        
        // Reload to update the UI
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        showError('Trial Activation', response.message || 'Failed to activate trial. Please try again.');
      }
    } catch (error) {
      console.error('Error activating trial:', error);
      showError('Trial Activation', 'An error occurred while activating trial. Please try again.');
    } finally {
      setIsActivatingTrial(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    setError('');
    
    try {
      const response = await apiService.cancelSubscription();
      
      if (response.status) {
        // Force refresh restaurant details to reflect the cancellation status
        await loadRestaurantDetails(true);

        setShowCancelDialog(false);
        // Show success message - plan is cancelled but still active until billing period ends
        console.log('Subscription cancelled. Plan remains active until billing period ends.');
        showSuccess('Subscription',  'Subscription cancelled successfully. Your plan will remain active until the end of the current billing period.');
        onClose();


        await loadRestaurantDetails(true);
        window.location.reload();
        
      } else {
        showError('Subscription', response.message || 'Failed to cancel subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      showError('Subscription', 'An error occurred while cancelling subscription. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  // Handle browser back button to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = () => {
      onClose();
    };

    // Push history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  // Handle browser back button for cancel dialog
  useEffect(() => {
    if (!showCancelDialog) return;

    const handlePopState = () => {
      if (!isCancelling) {
        setShowCancelDialog(false);
      }
    };

    // Push history state when dialog opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showCancelDialog, isCancelling]);

  // Refresh restaurant details when modal opens to ensure we have latest subscription status
  useEffect(() => {
    if (!isOpen) {
      hasLoadedDetailsRef.current = false;
      hasFetchedPlansRef.current = false;
      return;
    }

    if (hasLoadedDetailsRef.current) {
      return;
    }

    hasLoadedDetailsRef.current = true;
    loadRestaurantDetails(true);
  }, [isOpen, loadRestaurantDetails]);


  // Auto-dismiss error messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000); // 5 seconds

      // Cleanup timer on unmount or when error changes
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Get current plan from restaurant details
  const rawCurrentPlan = (restaurantDetails as any)?.plan as 'basic' | 'pro' | 'no active plan' | 'no_plan' | undefined;
  const isTrialMode = Boolean((restaurantDetails as any)?.trial_mode);
  const isSubscriptionCancelled = Boolean((restaurantDetails as any)?.razorpay_subscription_cancelled);
  
  // If trial_mode is true, treat plan as no_plan (user needs to upgrade)
  const currentPlan = isTrialMode ? 'no_plan' : rawCurrentPlan;
  
  const hasPlan = currentPlan === 'basic' || currentPlan === 'pro';
  const hasActivePlan = hasPlan;
  const shouldShowPlans = !hasPlan;
  const shouldForcePlanSelection = !hasPlan;

  // Fetch plans from API if enable_payment is true
  useEffect(() => {
    const fetchPlans = async () => {
      if (!isOpen) return;

      if (hasPlan) {
        // No need to fetch plans when user already has an active plan
        hasFetchedPlansRef.current = false;
        setPlansData([]);
        return;
      }

      if (!enablePayment) {
        hasFetchedPlansRef.current = false;
        const defaultPlans = getDefaultPlans(true);
        setPlansData(defaultPlans);
        return;
      }

      // Reset fetch flag in trial mode to ensure plans are fetched
      if (isTrialMode) {
        hasFetchedPlansRef.current = false;
      }

      if (hasFetchedPlansRef.current) {
        return;
      }

      hasFetchedPlansRef.current = true;

      setLoadingPlans(true);
      try {
        const response = await apiService.getPlans();

        // Type assertion for the response data
        const responseData = response as any;

        console.log('Plans API Response (full):', response);
        console.log('Response status:', response.status);
        console.log('Response plans:', responseData.plans);
        console.log('Response data keys:', Object.keys(responseData));

        // Check for plans in multiple possible locations
        const allPlans = responseData.plans || (responseData as any).data?.plans || [];

        if (response.status && allPlans && allPlans.length > 0) {
          // Filter out disabled plans - only show plans with status !== 'disabled'
          const enabledPlans = allPlans.filter((plan: any) => plan.status !== 'disabled');
          console.log('Found plans (all):', allPlans);
          console.log('Enabled plans (status !== disabled):', enabledPlans);
          
          if (enabledPlans.length > 0) {
            // Map API plans to existing plan structure
            const mappedPlans = mapApiPlansToExistingPlans(enabledPlans);
            console.log('Mapped Plans:', mappedPlans);
            setPlansData(mappedPlans);
          } else {
            console.warn('No enabled plans found after filtering disabled plans. Using default plans as fallback.');
            // Fallback to default plans if all API plans are disabled
            const defaultPlans = getDefaultPlans(true);
            setPlansData(defaultPlans);
          }
        } else {
          console.error('Failed to fetch plans or no plans found. Response:', response);
          // Fallback to default plans
          const defaultPlans = getDefaultPlans(true);
          setPlansData(defaultPlans);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        // Fallback to default plans
        const defaultPlans = getDefaultPlans(true);
        setPlansData(defaultPlans);
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, [isOpen, enablePayment, currentPlan, hasPlan, isTrialMode]);

  const mapApiPlansToExistingPlans = (apiPlans: Array<{ id: string; name: string; amount: number; status?: string }>): PlanData[] => {
    // Always get all plans for mapping (basic and pro) regardless of showOnlyPro
    const defaultPlans = getDefaultPlans(true);
    
    console.log('Default Plans:', defaultPlans);
    console.log('API Plans (already filtered for enabled only):', apiPlans);
    
    // Map API plans to default plans by comparing lowercase names
    // Only return plans that have a matching API plan (enabled plans only)
    const mappedPlans: PlanData[] = [];
    
    defaultPlans.forEach(defaultPlan => {
      // Try to match by lowercase name (remove " Plan" from default plan name)
      const defaultPlanNameLower = defaultPlan.name.toLowerCase().replace(/\s*plan\s*/gi, '').trim();
      const matchingApiPlan = apiPlans.find(
        apiPlan => {
          const apiPlanNameLower = (apiPlan.name || '').toLowerCase().trim();
          // Try exact match first
          if (apiPlanNameLower === defaultPlanNameLower) {
            return true;
          }
          // Try matching without "plan" suffix/prefix
          const apiPlanNameNoPlan = apiPlanNameLower.replace(/\s*plan\s*/gi, '').trim();
          const defaultPlanNameNoPlan = defaultPlanNameLower.replace(/\s*plan\s*/gi, '').trim();
          if (apiPlanNameNoPlan === defaultPlanNameNoPlan) {
            return true;
          }
          // Try partial match (contains)
          if (apiPlanNameLower.includes(defaultPlanNameNoPlan) || defaultPlanNameNoPlan.includes(apiPlanNameLower)) {
            return true;
          }
          return false;
        }
      );
      
      if (matchingApiPlan) {
        // Plans with status 'disabled' are already filtered out before mapping
        // So we only process enabled plans here
        const mappedPlan: PlanData = {
          ...defaultPlan,
          planId: matchingApiPlan.id || '', // Handle missing id
          price: `₹${matchingApiPlan.amount}`,
          period: '/month',
          comingSoon: false,
          buttonText: defaultPlan.buttonText
        };
        mappedPlans.push(mappedPlan);
      }
    });
    
    return mappedPlans;
  };

  // Helper function to get shop URL - memoized to update when restaurantDetails changes
  const shopUrl = useMemo(() => {
    try {
      const shopId = (restaurantDetails as any)?.id;
      if (shopId) {
        return buildRestaurantFrontendOrigin({ id: shopId });
      }
    } catch (error) {
      console.warn('Failed to build shop URL', error);
    }
    return 'yourshopname.menunix.com';
  }, [restaurantDetails]);

  const getDefaultPlans = (includeAll: boolean = false): PlanData[] => {
    // If includeAll is true, always return both plans (for filtering later)
    if (includeAll) {
      return [
        {
          id: 'basic' as const,
          name: 'Basic Plan',
          color: 'success',
          description: 'Launch your store instantly with Menunix.',
          features: [
            '✅ Unlimited products, categories & variants',
            '✅ Upload product images (image library support)',
            '✅ 1-Tap "Order on WhatsApp" button on every product',
            '✅ Easy Menunix Dashboard for managing your store',
            `✅ Branded store link (${shopUrl})`,
            '✅ Custom themes that match your brand colors',
            '✅ Upload your logo, business details & contact info',
            '✅ Show / Hide price + Enquiry-only mode',
            '✅ In-stock / Sold-out / Hidden visibility options',
            '✅ PWA store app — customers can install your shop on their phone',
            '✅ Fast-loading pages optimized for mobile',
            '✅ Built-in SEO optimization for Google ranking (editable)',
            '✅ Free SSL secure hosting included'
          ],
          buttonText: 'Start Now',
          iconName: 'store',
          popular: false
        },
        {
          id: 'pro' as const,
          name: 'Pro Plan',
          color: 'primary',
          description: 'Ideal for restaurants and cafés with table service.',
          features: [
            'Includes everything in Basic, plus:',
            '🍽️ Table Order System',
            '📊 Order Reports & Analytics',
            '🧾 Manage Active Orders from Dashboard',
            '🔢 Unique QR Code for Each Table',
            '⏱️ Real-time Order Status (e.g. Pending, Served, etc.)'
          ],
          buttonText: 'Start with Pro',
          iconName: 'restaurant',
          popular: false
        }
      ];
    }
    
    return showOnlyPro ? [
      {
        id: 'pro' as const,
        name: 'Pro Plan',
        color: 'primary',
        description: 'Ideal for restaurants and cafés with table service.',
        features: [
          'Includes everything in Basic, plus:',
          '🍽️ Table Order System',
          '📊 Order Reports & Analytics',
          '🧾 Manage Active Orders from Dashboard',
          '🔢 Unique QR Code for Each Table',
          '⏱️ Real-time Order Status (Pending / Served,etc)'
        ],
        buttonText: 'Upgrade to Pro',
        iconName: 'restaurant',
        popular: false
      }
    ] : [
      {
        id: 'basic' as const,
        name: 'Basic Plan',
        color: 'success',
        description: 'Perfect for small shops, boutiques, and local businesses.',
        features: [
          '✅ Digital Catalog / Menu',
          '✅ Unlimited Products & Categories',
          '✅ "Order on WhatsApp" Button',
          '✅ Menunix Dashboard Access',
          `✅ Shareable Shop Link (e.g. ${shopUrl})`,
          '✅ Upload Shop Logo & Details',
          '✅ Custom Theme (match your brand colors & style)',
          "✅ Support anytime via Dashboard → Help Center"
        ],
        buttonText: 'Start with Basic',
        iconName: 'store',
        popular: false
      },
      {
        id: 'pro' as const,
        name: 'Pro Plan',
        color: 'primary',
        description: 'Ideal for restaurants and cafés with table service.',
        features: [
          'Includes everything in Basic, plus:',
          '🍽️ Table Order System',
          '📊 Order Reports & Analytics',
          '🧾 Manage Active Orders from Dashboard',
          '🔢 Unique QR Code for Each Table',
          '⏱️ Real-time Order Status (e.g. Pending, Served, etc.)'
        ],
        buttonText: 'Start with Pro',
        iconName: 'restaurant',
        popular: false
      }
    ];
  };

  const initializeDefaultPlans = () => {
    // Always initialize with all plans, filtering happens later based on current plan
    setPlansData(getDefaultPlans(true));
  };

  // Initialize default plans on mount only if enable_payment is false
  useEffect(() => {
    if (plansData.length === 0) {
      const enablePayment = (restaurantDetails as any)?.enable_payment;
      if (!enablePayment) {
        initializeDefaultPlans();
      }
    }
  }, []);

  // Helper function to render icon
  const renderIcon = (iconName: 'store' | 'restaurant') => {
    const IconComponent = iconName === 'store' ? BuildingStorefrontIcon : TableCellsIcon;
    return <IconComponent className="h-6 w-6" />;
  };

  // Get status display text and color
  const getStatusDisplay = () => {
    // Show trial status if in trial mode
    if (isTrialMode) {
      return { text: 'Free Trial (15 Days)', color: 'bg-purple-500' };
    }
    
    if (!hasPlan) {
      if (currentPlan === 'no active plan') {
        return { text: 'No Active Plan', color: 'bg-yellow-500' };
      }
      return { text: 'No Plan Selected', color: 'bg-gray-500' };
    }

    if (currentPlan === 'basic') {
      return { text: 'Active', color: 'bg-green-500' };
    }

    if (currentPlan === 'pro') {
      return { text: 'Pro Plan', color: 'bg-orange-600' };
    }

    return { text: 'No Plan Selected', color: 'bg-gray-500' };
  };
  
  const statusDisplay = getStatusDisplay();

  const showLogoutButton = shouldForcePlanSelection && !isTrialMode;
  const showCloseButton = !isMandatory || hasActivePlan || !enablePayment;
  const shouldRenderModal = isOpen && (shouldForcePlanSelection || !isMandatory || hasActivePlan);
  const shouldShowCurrentPlanCard = currentPlan !== 'no_plan';

  // Filter plans based on current plan and showOnlyPro
  // Use plansData if available (contains only enabled API plans)
  // Fallback to default plans if plansData is empty
  let availablePlans = plansData.length > 0 
    ? plansData 
    : getDefaultPlans(true); // Fallback to default plans
  
  // Filter out disabled/coming soon plans (should already be filtered, but double-check)
  availablePlans = availablePlans.filter(plan => !plan.comingSoon);
  
  // In trial mode, always show plans (user needs to upgrade)
  // Otherwise, hide plans if user already has a plan
  if (hasPlan && !isTrialMode) {
    availablePlans = [];
  } else if (showOnlyPro && !isTrialMode) {
    // Only apply showOnlyPro filter when not in trial mode
    // In trial mode, show all available plans so user can choose
    availablePlans = availablePlans.filter(plan => plan.id === 'pro');
  }
  
  const plans = availablePlans;

  // Calculate header subtitle text
  const getHeaderSubtitle = () => {
    if (shouldShowPlans && plans.length === 1) {
      return 'Subscribe to get started with Menunix';
    }
    return 'Choose the perfect plan for your business';
  };

  const headerSubtitle = getHeaderSubtitle();

  // Debug: Log final plans to see if prices are present
  console.log('Current plan:', currentPlan);
  console.log('Final plans for rendering:', plans);
  console.log('Plans data state:', plansData);
  console.log('Header subtitle:', headerSubtitle, 'plans.length:', plans.length, 'shouldShowPlans:', shouldShowPlans);

  if (!shouldRenderModal) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 !mt-0"
        onClick={(e) => {
          if (!isMandatory || hasActivePlan) {
            if (e.target === e.currentTarget) {
              handleClose(true);
            }
          }
        }}
      >
        <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-orange-600 px-6 py-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 flex-1">
                {showLogoutButton && (
                  <button
                    onClick={logout}
                    className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
                    title="Logout"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 text-white" />
                  </button>
                )}
                <div className="flex-1 text-left md:text-left">
                  {!hasPlan && (<h2 className="block md:hidden text-xl md:text-2xl font-bold text-white">
                    💰 Subscribe to continue
                  </h2>) }
                   {hasPlan && (<h2 className="block md:hidden text-xl md:text-2xl font-bold text-white">
                    💰  Menunix Pricing Plans
                  </h2>) }
                  <h2 className="hidden md:block text-xl md:text-2xl font-bold text-white">
                    💰 Menunix Pricing {shouldShowPlans && plans.length === 1 ? 'Plan' : 'Plans'}
                  </h2>
                  <p className="hidden md:block text-sm md:text-base text-white/90 mt-1">
                    {headerSubtitle}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {showLogoutButton && (
                  <button
                    onClick={logout}
                    className="hidden md:flex items-center gap-2 px-4 py-2 border border-white text-white rounded-lg hover:bg-white/10 transition-colors text-sm"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    Logout
                  </button>
                )}
                {showCloseButton && (
                  <button
                    onClick={() => handleClose(true)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6 text-white" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}
            
            {/* Current Plan Display - Hide when plan is explicitly "no_plan" */}
            {shouldShowCurrentPlanCard && (
              <div className="mb-6 p-4 bg-gradient-to-r from-orange-600 to-pink-600 rounded-lg text-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-bold mb-2">📦 Your subscription</h3>
                    <h4 className="text-xl md:text-2xl font-bold mb-2">
                      {isTrialMode
                        ? 'Free Trial'
                        : currentPlan === 'basic' 
                        ? '' 
                        : currentPlan === 'pro' 
                          ? 'Pro Plan' 
                          : 'No Active Plan'}
                    </h4>
                    <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                      <span className="text-sm md:text-base opacity-90">Subscription Status:</span>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs md:text-sm font-bold text-white ${statusDisplay.color}`}>
                        {statusDisplay.text}
                      </span>
                    </div>
                    <h2 className="text-lg mb-2">
                      {isTrialMode
                        ? 'You are on a 15-day free trial. Upgrade to continue using all features after the trial period ends.'
                        : !hasPlan
                        ? 'Subscribe to get started with Menunix.'
                        : isSubscriptionCancelled
                          ? 'Your subscription is cancelled. Plan remains active until the end of the current billing period.'
                          : ''}
                    </h2>
                    {isSubscriptionCancelled && hasPlan && (
                      <div className="mt-3 p-3 bg-yellow-500/20 border border-yellow-400/30 rounded-lg">
                        <p className="text-sm md:text-base text-white">
                          Subscription cancelled. Your plan will remain active until the end of the current billing period.
                        </p>
                      </div>
                    )}
                  </div>
                  {enablePayment &&
                   hasPlan && 
                   !isSubscriptionCancelled && (
                    <button
                      onClick={() => setShowCancelDialog(true)}
                      className="px-4 py-2 border-2 border-white text-white rounded-lg hover:bg-white/10 transition-colors flex items-center gap-2 font-semibold"
                    >
                      <XCircleIcon className="h-5 w-5" />
                      Cancel Subscription
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {loadingPlans && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              </div>
            )}
            
            {/* Free Trial Option - Show when enable_skip and enable_payment are true, user has no plan, and not already in trial mode */}
            {shouldShowPlans && enableSkip && enablePayment && !isTrialMode && (
              <div className="mb-6 p-6 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl text-white shadow-lg">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🎁</span>
                      <h3 className="text-xl md:text-2xl font-bold">15-Day Free Trial</h3>
                    </div>
                    <p className="text-base md:text-lg mb-3 opacity-95">
                      Try all features free for 15 days! No credit card required.
                    </p>
                    <p className="text-sm md:text-base opacity-90">
                      After 15 days, upgrade to continue using Menunix.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={handleTrialActivation}
                      disabled={isActivatingTrial || isLoading}
                      className="w-full md:w-auto px-6 py-3 bg-white text-purple-600 rounded-lg font-bold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isActivatingTrial ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                          <span>Activating...</span>
                        </>
                      ) : (
                        'Start Free Trial'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Plans Grid */}
            {shouldShowPlans && (
              <div className={`grid gap-6 ${
                plans.length === 1 
                  ? 'grid-cols-1 justify-items-center' 
                  : 'grid-cols-1 md:grid-cols-2'
              }`}>
                {!loadingPlans && plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative border-2 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex flex-col ${
                      plans.length === 1 ? 'max-w-md w-full' : 'w-full'
                    } ${
                      selectedPlan === plan.id 
                        ? (plan.color === 'primary' ? 'border-orange-600 shadow-xl' : 'border-green-600 shadow-xl')
                        : 'border-gray-300 shadow-md'
                    }`}
                  >
                    {plan.popular && (
                      <span className="absolute top-3 right-3 bg-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                        Most Popular
                      </span>
                    )}

                    {/* Plan Header */}
                    <div className="text-center mb-2 mt-4">



                      
                    {/* <h3 className="text-2xl font-bold mb-2">{plan.name}</h3> */}
                      
                      {plan.comingSoon ? (
                        <div className="mb-4">
                          <span className="inline-block px-4 py-1 bg-gray-500 text-white text-sm font-bold rounded-full">
                            Coming Soon
                          </span>
                        </div>
                      ) : (
                        plan.price && plan.period && (
                          <div className="mb-4">
                            <span className={`text-4xl font-bold ${
                              plan.color === 'primary' ? 'text-orange-600' : 'text-green-600'
                            }`}>
                              {plan.price}
                            </span>
                            <span className="text-lg text-gray-600 ml-2">{plan.period}</span>
                          </div>
                        )
                      )}
                      
                      <p className="text-gray-600 text-base mb-4 text-bold">{plan.description}</p>
                    </div>

                    <div className="border-t border-gray-200 my-4"></div>

                    {/* Features */}
                    <ul className="flex-1 mb-6 space-y-2">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="text-sm text-gray-700 flex items-start">
                          <span className="mr-2">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Button */}
                    {plan.comingSoon ? (
                      <button
                        disabled
                        className={`w-full py-3 px-4 rounded-lg font-semibold cursor-not-allowed opacity-70 border-2 ${
                          plan.color === 'primary'
                            ? 'border-orange-600 text-orange-600'
                            : 'border-green-600 text-green-600'
                        }`}
                      >
                        Coming Soon
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePlanSelect(plan.id)}
                        disabled={isLoading}
                        className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                          plan.color === 'primary'
                            ? 'bg-orange-600 hover:bg-orange-700 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {isLoading && selectedPlan === plan.id ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>{isVerifyingPayment ? 'Verifying payment...' : 'Processing...'}</span>
                          </div>
                        ) : (
                          plan.buttonText
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Plan switch tip - only show when plans are visible */}
            {shouldShowPlans && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  💡 {plans.length === 1 
                    ? 'UPI AutoPay will debit ₹199 every month. Cancel anytime.' 
                    : 'UPI AutoPay will debit ₹199 every month. Cancel anytime.'}
                </p>
              </div>
            )}

            {/* Free Service Notice - only show if payment is not enabled */}
            {!(restaurantDetails as any)?.enable_payment && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-bold text-blue-900 mb-1">Service Notice</h4>
                <p className="text-sm text-blue-800">
                  This service is free now, but may require payment later.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Subscription Confirmation Dialog */}
      {showCancelDialog && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60] !mt-0"
          onClick={(e) => {
            if (!isCancelling && e.target === e.currentTarget) {
              if (window.history.state?.modalOpen) {
                window.history.back();
              }
              setShowCancelDialog(false);
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-red-600 px-6 py-4 rounded-t-xl">
              <div className="flex items-center gap-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                <h2 className="text-xl font-bold text-white">Cancel Subscription</h2>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-bold text-yellow-900 mb-2">⚠️ Important Warning</h3>
                <p className="text-sm text-yellow-800 mb-2">By cancelling your subscription:</p>
                <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1 mb-2">
                  <li>Your subscription will remain active until the end of the current billing period</li>
                  <li>
                    <strong>After the subscription period ends:</strong>
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      <li>❌ You will lose access to the Dashboard</li>
                      <li>❌ Your shop website will no longer be accessible</li>
                      <li>❌ All features will be disabled</li>
                    </ul>
                  </li>
                  <li>You can resubscribe anytime, but your data may be affected</li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to cancel your subscription? This action cannot be undone easily.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col-reverse md:flex-row gap-3 md:justify-end">
                <button
                  onClick={() => {
                    if (!isCancelling) {
                      if (window.history.state?.modalOpen) {
                        window.history.back();
                      }
                      setShowCancelDialog(false);
                    }
                  }}
                  disabled={isCancelling}
                  className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={isCancelling}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2"
                >
                  {isCancelling ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-5 w-5" />
                      Yes, Cancel Subscription
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PricingPlansModal;
